import { Badge, Box, Flex, Icon, Text, Timeline } from "@chakra-ui/react-v3";
import { Condition } from "@medplum/fhirtypes";
import { getDocIdExtension, truncateDocId } from "../utils/document-utils";
import { FaSnowflake, FaLeaf, FaSeedling, FaSun, FaCircle } from "react-icons/fa";
import dayjs, { Dayjs } from "dayjs";
import {
  getConditionCategory,
  getEndTime,
  getOnsetTime,
} from "../../shared-components/fhir-sections/conditions/table-data";

type Season = "winter" | "spring" | "summer" | "fall";

type SeasonConfig = {
  icon: React.ElementType;
  color: string;
};

const SEASON_CONFIGS: Record<Season, SeasonConfig> = {
  winter: {
    icon: FaSnowflake,
    color: "#7BA3BC",
  },
  spring: {
    icon: FaSeedling,
    color: "#7EAB92",
  },
  summer: {
    icon: FaSun,
    color: "#D4936A",
  },
  fall: {
    icon: FaLeaf,
    color: "#B88A68",
  },
};

function getSeason(date: Dayjs): Season {
  const month = date.month(); // 0-11
  const day = date.date();

  /** Winter: Dec 21 - Mar 20 */
  if (month === 11 && day >= 21) return "winter";
  if (month === 0 || month === 1) return "winter";
  if (month === 2 && day <= 20) return "winter";

  /** Spring: Mar 21 - Jun 20 */
  if (month === 2 && day >= 21) return "spring";
  if (month === 3 || month === 4) return "spring";
  if (month === 5 && day <= 20) return "spring";

  /** Summer: Jun 21 - Sep 20 */
  if (month === 5 && day >= 21) return "summer";
  if (month === 6 || month === 7) return "summer";
  if (month === 8 && day <= 20) return "summer";

  /** Fall: Sep 21 - Dec 20 */
  return "fall";
}

export type OccurrencesProps = {
  conditions: Condition[];
  patientDob?: string;
};

/**
 * Renders a list of condition occurrences as a timeline.
 * The first occurrence (most recent) is highlighted.
 * Conditions are sorted by date with newest first.
 * Year separators are added to show when years change.
 */
export function Occurrences({ conditions, patientDob }: OccurrencesProps) {
  if (!conditions || conditions.length === 0) {
    return null;
  }

  const sortedConditions = [...conditions].sort((a, b) => {
    const dateA = getOnsetTime(a, patientDob) ?? getEndTime(a, patientDob) ?? "";
    const dateB = getOnsetTime(b, patientDob) ?? getEndTime(b, patientDob) ?? "";

    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;

    return new Date(dateB).getTime() - new Date(dateA).getTime();
  });

  return (
    <Box>
      <Text fontSize="lg" fontWeight="semibold" mb={3}>
        Occurrences
      </Text>
      <Box
        position="relative"
        _after={{
          content: '""',
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "60px",
          background: "linear-gradient(to top, var(--chakra-colors-bg), transparent)",
          pointerEvents: "none",
        }}
      >
        <Box
          maxH="400px"
          overflowY="auto"
          pr={2}
          css={{
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "var(--chakra-colors-bg-subtle)",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "var(--chakra-colors-border-emphasized)",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "var(--chakra-colors-fg-muted)",
            },
          }}
        >
          <Timeline.Root>
            {sortedConditions.map((condition, index) => {
              const currentDate =
                getOnsetTime(condition, patientDob) ?? getEndTime(condition, patientDob);
              const currentYear = currentDate ? dayjs(currentDate).year() : undefined;

              const previousCondition = index > 0 ? sortedConditions[index - 1] : undefined;
              const previousDate = previousCondition
                ? getOnsetTime(previousCondition, patientDob) ??
                  getEndTime(previousCondition, patientDob)
                : undefined;
              const previousYear = previousDate ? dayjs(previousDate).year() : undefined;

              const nextCondition =
                index < sortedConditions.length - 1 ? sortedConditions[index + 1] : undefined;
              const nextDate = nextCondition
                ? getOnsetTime(nextCondition, patientDob) ?? getEndTime(nextCondition, patientDob)
                : undefined;
              const nextYear = nextDate ? dayjs(nextDate).year() : undefined;

              const showYearDivider =
                currentYear && (index === 0 || (previousYear && currentYear !== previousYear));
              const isLastInYear = !nextYear || nextYear !== currentYear;

              return (
                <Box key={condition.id ?? index}>
                  {showYearDivider && (
                    <Box
                      position="sticky"
                      top={0}
                      zIndex={10}
                      bg="bg"
                      mb={2}
                      borderTop="2px solid"
                      borderColor="border.emphasized"
                    >
                      <Text fontSize="sm" fontWeight="semibold" color="fg.muted" mt={1.5} mb={1}>
                        {currentYear}
                      </Text>
                    </Box>
                  )}
                  <OccurrenceItem
                    condition={condition}
                    patientDob={patientDob}
                    isFirst={index === 0}
                    currentDate={currentDate}
                    isLastInYear={isLastInYear}
                  />
                </Box>
              );
            })}
          </Timeline.Root>
        </Box>
      </Box>
    </Box>
  );
}

type OccurrenceItemProps = {
  condition: Condition;
  patientDob?: string;
  isFirst: boolean;
  currentDate: string | undefined;
  isLastInYear: boolean;
};

function OccurrenceItem({
  condition,
  patientDob,
  isFirst,
  currentDate,
  isLastInYear,
}: OccurrenceItemProps) {
  const start = getOnsetTime(condition, patientDob);
  const end = getEndTime(condition, patientDob);
  const category = getConditionCategory(condition);
  const docId = getDocIdExtension(condition);

  const formattedStart = start ? dayjs(start).format("MMM D, YYYY") : "-";
  const formattedEnd = end ? dayjs(end).format("MMM D, YYYY") : "-";

  const dateRange =
    end && formattedStart !== formattedEnd ? `${formattedStart} - ${formattedEnd}` : formattedStart;

  /** Get ICD-10 coding information from the condition */
  const icd10Codings = condition.code?.coding?.filter(
    coding =>
      coding.code &&
      coding.display &&
      (coding.system?.includes("icd-10") || coding.system?.includes("ICD-10"))
  );

  /** Get season-based styling */
  const season = currentDate ? getSeason(dayjs(currentDate)) : undefined;
  const seasonConfig = season ? SEASON_CONFIGS[season] : undefined;
  const SeasonIcon = seasonConfig?.icon ?? FaCircle;
  const iconColor = seasonConfig?.color ?? (isFirst ? "colorPalette.600" : "gray.400");

  return (
    <Timeline.Item>
      <Timeline.Connector>
        <Timeline.Separator />
        <Timeline.Indicator bg="transparent" asChild mr={-1}>
          <Icon fontSize="lg" color={iconColor} opacity={0.7}>
            <SeasonIcon />
          </Icon>
        </Timeline.Indicator>
      </Timeline.Connector>
      <Timeline.Content>
        <Box
          pb={isLastInYear ? 0 : 3}
          borderBottom={isLastInYear ? "none" : "1px solid"}
          borderColor="border"
        >
          <Flex justify="space-between" align="start" mb={2}>
            <Timeline.Title
              color={isFirst ? "colorPalette.700" : "fg.emphasized"}
              fontWeight={isFirst ? "semibold" : "medium"}
            >
              {dateRange}
            </Timeline.Title>
            {category !== "-" && (
              <Badge colorPalette={category === "Encounter Diagnosis" ? "blue" : "gray"} size="sm">
                {category}
              </Badge>
            )}
          </Flex>

          {icd10Codings && icd10Codings.length > 0 && (
            <Box mt={2}>
              {icd10Codings.map((coding, idx) => (
                <Flex key={idx} gap={2} alignItems="baseline" mt={1}>
                  <Text fontSize="sm" color="fg.emphasized" fontFamily="mono">
                    {coding.code}
                  </Text>
                  <Text fontSize="sm" color="fg.muted">
                    {coding.display}
                  </Text>
                </Flex>
              ))}
            </Box>
          )}

          {docId && (
            <Text fontSize="xs" color="fg.muted" mt={2}>
              Source: {truncateDocId(docId, 30)}
            </Text>
          )}
        </Box>
      </Timeline.Content>
    </Timeline.Item>
  );
}
